import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type CreateCustomerPdfAwardRequest,
  type CreateCustomerPdfCelebrityRequest,
  type CreateCustomerPdfStatRequest,
  type UpdateCustomerPdfAwardRequest,
  type UpdateCustomerPdfCelebrityRequest,
  type UpdateCustomerPdfStatRequest,
} from '@utazzo/shared';
import { toast } from 'sonner';

import * as api from './customer-pdf.api';

/** Root key: every mutation invalidates the whole feature (lists + preview). */
const ROOT = ['customer-pdf'] as const;

const QK = {
  celebrities: ['customer-pdf', 'celebrities'] as const,
  awards: ['customer-pdf', 'awards'] as const,
  partnerLogos: ['customer-pdf', 'partner-logos'] as const,
  stats: ['customer-pdf', 'stats'] as const,
  publicContent: ['customer-pdf', 'public-content'] as const,
};

const extractMessage = (err: unknown): string | undefined => {
  if (!err || typeof err !== 'object') return undefined;
  const e = err as { response?: { data?: { message?: string | string[] } }; message?: string };
  const m = e.response?.data?.message;
  if (Array.isArray(m)) return m.join('; ');
  return m ?? e.message;
};

// ---------------------------- Celebrities ----------------------------

export const useCelebritiesList = () =>
  useQuery({ queryKey: QK.celebrities, queryFn: api.listCelebrities, staleTime: 30_000 });

export const useCreateCelebrity = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCustomerPdfCelebrityRequest) => api.createCelebrity(payload),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ROOT });
      toast.success(`Celebrity ${c.name} added`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add celebrity'),
  });
};

export const useUpdateCelebrity = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCustomerPdfCelebrityRequest }) =>
      api.updateCelebrity(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROOT });
      toast.success('Celebrity updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update celebrity'),
  });
};

export const useDeleteCelebrity = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteCelebrity(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROOT });
      toast.success('Celebrity removed');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to remove celebrity'),
  });
};

// ---------------------------- Awards ----------------------------

export const useAwardsList = () =>
  useQuery({ queryKey: QK.awards, queryFn: api.listAwards, staleTime: 30_000 });

export const useCreateAward = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCustomerPdfAwardRequest) => api.createAward(payload),
    onSuccess: (a) => {
      qc.invalidateQueries({ queryKey: ROOT });
      toast.success(`Award "${a.title}" added`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add award'),
  });
};

export const useUpdateAward = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCustomerPdfAwardRequest }) =>
      api.updateAward(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROOT });
      toast.success('Award updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update award'),
  });
};

export const useDeleteAward = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteAward(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROOT });
      toast.success('Award removed');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to remove award'),
  });
};

// ---------------------------- Partner logos ----------------------------

export const usePartnerLogosList = () =>
  useQuery({ queryKey: QK.partnerLogos, queryFn: api.listPartnerLogos, staleTime: 30_000 });

export const useSetPartnerLogos = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (imageIds: string[]) => api.setPartnerLogos(imageIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROOT });
      toast.success('Partner logos saved');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to save partner logos'),
  });
};

// ---------------------------- Stats strip ----------------------------

export const useStatsList = () =>
  useQuery({ queryKey: QK.stats, queryFn: api.listStats, staleTime: 30_000 });

export const useCreateStat = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCustomerPdfStatRequest) => api.createStat(payload),
    onSuccess: (s) => {
      qc.invalidateQueries({ queryKey: ROOT });
      toast.success(`Stat "${s.value}" added`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add stat'),
  });
};

export const useUpdateStat = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCustomerPdfStatRequest }) =>
      api.updateStat(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROOT });
      toast.success('Stat updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update stat'),
  });
};

export const useDeleteStat = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteStat(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROOT });
      toast.success('Stat removed');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to remove stat'),
  });
};

// ---------------------------- Public content (preview) ----------------------------

export const useCustomerPdfPublicContent = () =>
  useQuery({ queryKey: QK.publicContent, queryFn: api.getPublicContent, staleTime: 0 });
